import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { faqItems } from "../data/mock";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";

export default function FAQPage() {
  const { faq: faqItems } = useSiteConfig();
  
  usePageSEO({
    title: "FAQ",
    description: "Frequently asked questions about polygraph examinations - accuracy, preparation, process, and what to expect from The Lie Detective's professional services.",
    path: "/faq"
  });
  
  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            FAQ
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Frequently Asked
            <br />
            <span className="text-[#DC2626]">Questions</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            Everything you need to know about polygraph testing. Can't find the
            answer you're looking for? Feel free to contact us directly.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-[#111118] border border-white/[0.04] rounded-2xl px-6 lg:px-8 overflow-hidden data-[state=open]:border-[#DC2626]/20 transition-colors duration-300"
              >
                <AccordionTrigger className="text-white text-left font-semibold text-base py-6 hover:no-underline hover:text-[#DC2626] transition-colors duration-200 [&>svg]:text-[#DC2626] [&>svg]:h-5 [&>svg]:w-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 leading-relaxed text-sm pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D0D14] py-24 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            We're here to help. Contact us for a confidential discussion about
            your specific needs and any questions you may have.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
